/**
 * Privacy Policy — RCA Tool
 * No auth required. Accessible at /privacy.
 */

import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Mail, Shield } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'

const EFFECTIVE_DATE = 'May 2, 2026'
const CONTACT_EMAIL = 'rca-support@morton-tech.com'

interface SectionProps {
  readonly heading: string
  readonly children: React.ReactNode
}

function Section({ heading, children }: SectionProps) {
  return (
    <section className="space-y-3">
      <h2 className="text-base font-semibold text-foreground">{heading}</h2>
      {children}
    </section>
  )
}

function Para({ children }: { readonly children: React.ReactNode }) {
  return <p className="text-sm text-muted-foreground leading-relaxed">{children}</p>
}

function Bullets({ items }: { readonly items: readonly string[] }) {
  return (
    <ul className="space-y-1.5 pl-4">
      {items.map((item) => (
        <li key={item} className="text-sm text-muted-foreground leading-relaxed list-disc">
          {item}
        </li>
      ))}
    </ul>
  )
}

export default function Privacy() {
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <header className="border-b border-border sticky top-0 z-10 bg-background/90 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="gap-2 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <Separator orientation="vertical" className="h-5" />
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">
              Privacy Policy
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 space-y-10">
        {/* Page heading */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold tracking-tight text-foreground">
            Privacy Policy
          </h1>
          <p className="text-sm text-muted-foreground">
            Effective date: {EFFECTIVE_DATE}
          </p>
        </div>

        <Separator />

        {/* Sections */}
        <Section heading="1. Who We Are">
          <Para>
            The Risk &amp; Complexity Assessment (RCA) Tool is operated by
            Morton Technology Consulting
            and Capital Technology Alliance. For privacy inquiries contact:{' '}
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="text-primary hover:underline"
            >
              {CONTACT_EMAIL}
            </a>
            .
          </Para>
        </Section>

        <Section heading="2. Data Stored in Supabase">
          <Para>
            The following data is stored in a Supabase PostgreSQL database:
          </Para>
          <Bullets
            items={[
              'User accounts: name, email address, and authentication credentials (managed by Supabase Auth).',
              'Projects: project name, description, department, and associated metadata.',
              'Assessments: all answers entered through the RCA Wizard, computed scores, risk bands, and governance flags.',
              'Audit history: who created or modified each record and when.',
              'Share tokens: anonymised, expiring tokens used for read-only report sharing.',
            ]}
          />
        </Section>

        <Section heading="3. No Third-Party Sharing">
          <Para>
            Assessment data is not shared with any third parties, advertisers,
            or analytics providers. Data is visible only to authenticated users
            with an approved role (admin, CIO, director, PM, or viewer) and
            to system administrators. Stakeholder share links expose only the
            specific assessment report they reference and expire automatically.
          </Para>
        </Section>

        <Section heading="4. AI Features">
          <Para>
            When you use the AI narrative or recommendation feature, assessment
            data is sent to OpenAI via our Cloudflare Pages Function. Prompts
            include only the structured assessment scores — no names or
            personally identifiable information are included in AI prompts.
            OpenAI&apos;s data usage policy governs that processing; see
            openai.com/policies/privacy-policy.
          </Para>
        </Section>

        <Section heading="5. Data Retention">
          <Bullets
            items={[
              'Active account data: retained indefinitely while the account is active.',
              'Deleted accounts: removed within 30 days of a verified deletion request.',
              'Assessment records: retained until the associated project is deleted or a deletion request is submitted.',
              'Share tokens: automatically expire per their configured duration (default 30 days).',
              'Server logs (Cloudflare): retained for up to 30 days per Cloudflare\'s standard log retention policy.',
            ]}
          />
        </Section>

        <Section heading="6. Your GDPR Rights">
          <Para>
            If you are subject to GDPR or similar privacy legislation, you have
            the right to:
          </Para>
          <Bullets
            items={[
              'Access: request a copy of the data we hold about you.',
              'Correction: request correction of inaccurate records.',
              'Erasure: request deletion of your account and associated data.',
              'Restriction: request that we limit processing of your data.',
              'Portability: request an export of your data in a machine-readable format.',
            ]}
          />
          <Para>
            Submit requests to:{' '}
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="text-primary hover:underline"
            >
              {CONTACT_EMAIL}
            </a>
            . We will respond within 30 days.
          </Para>
        </Section>

        <Section heading="7. Security">
          <Para>
            All data is transmitted over TLS 1.2+. The Supabase database is
            protected by row-level security (RLS) policies that ensure users
            can only access records scoped to their role. The Cloudflare Pages
            Function authentication endpoint uses SHA-256 hashed credentials.
            No plaintext passwords are stored.
          </Para>
        </Section>

        <Section heading="8. Changes to This Policy">
          <Para>
            We may update this policy as our practices change. Material changes
            will be communicated by updating the effective date above and, where
            appropriate, by direct notification to registered users.
          </Para>
        </Section>

        {/* Contact */}
        <div className="rounded-lg border border-border bg-muted/30 p-6 flex items-start gap-4">
          <Mail className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">
              Privacy questions or data requests
            </p>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="text-sm text-primary hover:underline"
            >
              {CONTACT_EMAIL}
            </a>
          </div>
        </div>
      </main>
    </div>
  )
}
